import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { sorted } from "src/helpers";
import { ReviewTimelineTick } from "src/store/api/generatedApi";

dayjs.extend(utc);

const GROUP_THRESHOLD_MS = 5 * 60 * 1000;

const isConsumption = (tick: ReviewTimelineTick): boolean =>
  tick.flags.includes("consumption");

const isRailcar = (tick: ReviewTimelineTick): boolean =>
  tick.flags.includes("railcar_arrival") ||
  tick.flags.includes("railcar_departure");

const isAlwaysShown = (tick: ReviewTimelineTick): boolean =>
  isRailcar(tick) || (tick.verified && tick.comment !== null);

/**
 * Reduce ticks to roughly one per 5-minute group for rendering. Railcars and
 * verified commented events are always kept; each remaining group keeps its
 * earliest event plus one consumption drop. Apply after filtering so a matching
 * event is never thinned away in favour of a non-matching one.
 */
export const thinTimelineTicks = (
  ticks: ReviewTimelineTick[]
): ReviewTimelineTick[] => {
  const alwaysShown = ticks.filter(isAlwaysShown);
  const alwaysShownIds = new Set(alwaysShown.map((tick) => tick.id));
  const toSample = ticks.filter((tick) => !alwaysShownIds.has(tick.id));

  const sortedTicks = sorted(toSample, (tick) =>
    dayjs.utc(tick.start).valueOf()
  );

  // Single pass over the sorted ticks: a tick joins the current group while it
  // is within the threshold of that group's earliest (anchor) tick.
  const groups: ReviewTimelineTick[][] = [];
  let anchorMs: number | null = null;
  for (const tick of sortedTicks) {
    const tickMs = dayjs.utc(tick.start).valueOf();
    const currentGroup = groups[groups.length - 1];
    if (
      currentGroup !== undefined &&
      anchorMs !== null &&
      tickMs - anchorMs < GROUP_THRESHOLD_MS
    ) {
      currentGroup.push(tick);
    } else {
      groups.push([tick]);
      anchorMs = tickMs;
    }
  }

  const sampled: ReviewTimelineTick[] = [];
  for (const group of groups) {
    const representative = group[0];
    if (representative === undefined) continue;
    sampled.push(representative);
    const consumptionDrop = group.find(isConsumption);
    if (consumptionDrop !== undefined && consumptionDrop !== representative) {
      sampled.push(consumptionDrop);
    }
  }

  return [...alwaysShown, ...sampled];
};
